import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Wallet, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Forecast {
  days_ahead: number;
  expected_inflow_sar: number;
  expected_outflow_sar: number;
  net_sar: number;
  inflow_trip_count: number;
  draft_run_count: number;
}
interface OverdueRow {
  id: string; trip_code: string; rate_sar: string;
  expected_payment_date: string; invoiced_at: string | null;
  client_id: string | null; client_name: string | null;
  days_overdue: number;
}
interface DriverPipelineRun {
  id: string; driver_id: string; driver_name: string | null;
  period_month: string; basic_salary_sar: string;
  total_incentives_released_sar: string | null; total_due_sar: string;
  status: string;
}
interface DriverPipeline {
  draft_runs: DriverPipelineRun[];
  draft_total_sar: number;
  draft_run_count: number;
}

type Tab = 'incoming' | 'outgoing' | 'overdue' | 'reliability';

export default function CashFlowPage() {
  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [overdue, setOverdue] = useState<OverdueRow[]>([]);
  const [pipeline, setPipeline] = useState<DriverPipeline | null>(null);
  const [tab, setTab] = useState<Tab>('incoming');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [f, o, p] = await Promise.all([
        apiClient.get<Forecast>('/api/v1/cash-flow/forecast?daysAhead=30'),
        apiClient.get<OverdueRow[]>('/api/v1/cash-flow/overdue-invoices'),
        apiClient.get<DriverPipeline>('/api/v1/cash-flow/driver-pipeline'),
      ]);
      setForecast(f.data);
      setOverdue(o.data);
      setPipeline(p.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/flatbed"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Trips</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wallet className="h-4 w-4" /> Cash flow</div>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {forecast && (
          <div className="rounded-lg border border-border p-4 bg-muted/30">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Net {forecast.days_ahead} days</div>
            <div className={'text-3xl font-medium ' + (forecast.net_sar >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400')}>
              {fmtSar(forecast.net_sar)}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
              <div className="flex items-baseline gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                <span className="text-muted-foreground">Coming in:</span>
                <span className="font-mono">{fmtSar(forecast.expected_inflow_sar)}</span>
                <span className="text-xs text-muted-foreground">({forecast.inflow_trip_count} trips)</span>
              </div>
              <div className="flex items-baseline gap-2">
                <TrendingDown className="h-4 w-4 text-rose-600" />
                <span className="text-muted-foreground">Going out:</span>
                <span className="font-mono">{fmtSar(forecast.expected_outflow_sar)}</span>
                <span className="text-xs text-muted-foreground">({forecast.draft_run_count} draft runs)</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {([
            { v: 'incoming',     l: 'Incoming' },
            { v: 'outgoing',     l: 'Outgoing' },
            { v: 'overdue',      l: 'Overdue' },
            { v: 'reliability',  l: 'Client reliability' },
          ] as const).map((x) => (
            <button key={x.v} onClick={() => setTab(x.v)}
              className={'px-3 py-2 text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {x.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : (
          <>
            {tab === 'incoming' && (
              overdue.length === 0 && (!forecast || forecast.inflow_trip_count === 0) ? (
                <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No invoiced trips awaiting payment in next 30 days.</div>
              ) : (
                <div className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
                  Forecast above. Detailed per-trip view available in <Link className="underline" to="/dashboard/flatbed">trips list</Link> filtered by "Pending payment".
                </div>
              )
            )}

            {tab === 'outgoing' && (
              !pipeline || pipeline.draft_runs.length === 0 ? (
                <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No draft salary runs.</div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-xs text-muted-foreground bg-muted/40 border-b border-border">
                        <tr>
                          <th className="text-left px-3 py-2">Driver</th>
                          <th className="text-left px-3 py-2">Period</th>
                          <th className="text-right px-3 py-2">Basic</th>
                          <th className="text-right px-3 py-2">Incentive</th>
                          <th className="text-right px-3 py-2">Total due</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pipeline.draft_runs.map((r) => (
                          <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                            <td className="px-3 py-2">{r.driver_name ?? r.driver_id.slice(0, 8)}</td>
                            <td className="px-3 py-2 text-xs font-mono">{r.period_month}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtSar(Number(r.basic_salary_sar))}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtSar(Number(r.total_incentives_released_sar ?? 0))}</td>
                            <td className="px-3 py-2 text-right font-mono font-medium">{fmtSar(Number(r.total_due_sar))}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="text-sm border-t border-border bg-muted/30">
                        <tr>
                          <td colSpan={4} className="px-3 py-2 text-right text-xs uppercase tracking-wider text-muted-foreground">Total draft outflow</td>
                          <td className="px-3 py-2 text-right font-mono font-medium">{fmtSar(pipeline.draft_total_sar)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )
            )}

            {tab === 'overdue' && (
              overdue.length === 0 ? (
                <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No overdue invoices. 🎉</div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-xs text-muted-foreground bg-muted/40 border-b border-border">
                        <tr>
                          <th className="text-left px-3 py-2">Trip</th>
                          <th className="text-left px-3 py-2">Client</th>
                          <th className="text-left px-3 py-2">Expected</th>
                          <th className="text-right px-3 py-2">Days late</th>
                          <th className="text-right px-3 py-2">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overdue.map((o) => (
                          <tr key={o.id} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                            <td className="px-3 py-2 font-mono text-xs">
                              <Link to={'/dashboard/flatbed/' + o.id} className="hover:underline">{o.trip_code}</Link>
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {o.client_id ? (
                                <Link to={'/dashboard/flatbed/clients/' + o.client_id + '/payment-terms'} className="hover:underline">{o.client_name ?? o.client_id.slice(0, 8)}</Link>
                              ) : '—'}
                            </td>
                            <td className="px-3 py-2 text-xs">{o.expected_payment_date}</td>
                            <td className="px-3 py-2 text-right text-rose-700 dark:text-rose-400 font-medium">{o.days_overdue}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtSar(Number(o.rate_sar))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            )}

            {tab === 'reliability' && (
              <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
                Open a specific overdue invoice's client link (Overdue tab) to see their payment reliability + history. Per-client view at <code className="text-xs">/dashboard/flatbed/clients/:id/payment-terms</code>.
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
